import { useEffect, useRef, useState } from 'react'
import { X, Camera, AlertCircle } from 'lucide-react'
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library'

function BarcodeScanner({ onScan, onClose }) {
  const videoRef = useRef(null)
  const [error, setError] = useState(null)
  const [scanning, setScanning] = useState(true)
  const codeReaderRef = useRef(null)

  useEffect(() => {
    const codeReader = new BrowserMultiFormatReader()
    codeReaderRef.current = codeReader

    const startScanning = async () => {
      try {
        const videoInputDevices = await codeReader.listVideoInputDevices()
        
        if (videoInputDevices.length === 0) {
          setError('未检测到摄像头设备')
          return
        }

        let selectedDeviceId = null
        for (const device of videoInputDevices) {
          const label = device.label.toLowerCase()
          if (label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('后置')) {
            selectedDeviceId = device.deviceId
            break
          }
        }
        
        if (!selectedDeviceId) {
          selectedDeviceId = videoInputDevices[videoInputDevices.length - 1].deviceId
        }

        await codeReader.decodeFromVideoDevice(
          selectedDeviceId,
          videoRef.current,
          (result, err) => {
            if (result) {
              const code = result.getText()
              setScanning(false)
              codeReader.reset()
              onScan(code)
            }
            if (err && !(err instanceof NotFoundException)) {
              console.error('扫描错误:', err)
            }
          }
        )
      } catch (err) {
        console.error('摄像头访问错误:', err)
        setError('无法访问摄像头，请确保已授予摄像头权限')
      }
    }

    startScanning()

    return () => {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset()
      }
    }
  }, [onScan])

  const handleClose = () => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset()
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card max-w-lg w-full p-6 relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-4">
          <Camera className="w-12 h-12 text-green-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-gray-800">扫描条码</h2>
          <p className="text-gray-600 text-sm mt-1">将条码对准摄像头进行扫描</p>
        </div>

        {error ? (
          <div className="p-6 bg-red-50 border border-red-200 rounded-2xl text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={handleClose}
              className="mt-4 btn-secondary px-6 py-2"
            >
              关闭
            </button>
          </div>
        ) : (
          <div className="relative">
            <video
              ref={videoRef}
              className="w-full rounded-2xl bg-black"
              style={{ maxHeight: '400px' }}
            />
            {scanning && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-3/4 h-1 bg-green-400 animate-pulse rounded-full"></div>
              </div>
            )}
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <span className="inline-block px-4 py-2 bg-black/50 text-white rounded-full text-sm backdrop-blur-sm">
                {scanning ? '正在扫描...' : '扫描成功！'}
              </span>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-sm text-gray-500">
          支持 EAN-13、UPC-A、CODE-128 等常见条码格式
        </div>
      </div>
    </div>
  )
}

export default BarcodeScanner
